import type { ChatMessage } from "@/App/types/ChatMessage";
import type { User } from "@/App/user/User";
import type { Hospital } from "@/App/types/Hospital";
import type { Gender } from "@/Pages/Cases/types/gender";
import type { TagCategory } from "@/App/types/TagCategory";
import { CaseType } from "@/App/types/caseType";
import type { BidEms, BidHospital } from "@/App/types/bids/BidEms";
import type { TwiageCase } from "@/App/types/TwiageCase";
import { FeatureFlag } from "@/App/types/FeatureFlag";
import type { GpsLocation } from "@/App/types/GpsLocation";
import { BidStatus } from "@/Pages/Ift/components/bids/BidStatus";
import type { CaseAction } from "@/Pages/Cases/CaseAction";
import type { Complaint } from "@/App/types/Complaint";
import type { ComplaintSet } from "@/App/types/complaintSet";
import type { HasId } from "@/App/utils/updateInList";
import type { HasDynamicForms } from "@/App/types/HasDynamicForms";
import type { HasComplaintSet } from "@/App/types/HasComplaintSet";
import type { IftReportColumn } from "@/App/types/IftReportColumn";
import type { DefaultMapView } from "../components/Map/Map";
import type { DynamicFormFieldDefinition } from "@/App/types/dynamicFormFieldDefinition";

export { CaseType, FeatureFlag, BidStatus };
export type { Complaint, User, ChatMessage, Hospital, BidEms, BidHospital, TwiageCase, Gender, ComplaintSet };

export type AgeRange = {
  min: number;
  max: number;
};

export type Section = {
  id: string;
  name: string;
  tags?: Tag[];
};

export type SectionTag = {
  section: string;
  tags: Tag[];
};

export type Tag = {
  id: string;
  name: string;
  backgroundColor?: string;
  category?: TagCategory;
  groupIdentifier?: string;
  type?: string;
  data?: {
    name: string;
    type: string;
    key: string;
  }[];
};

export type DataEnabledTag = {
  id: string;
  name?: string;
  category?: string;
  type: string;
  groupIdentifier?: string;
  values?: DataEnabledTagValue[];
  updatedAt?: string;
};

export type DataEnabledTagValue = {
  updatedAt?: string;
  fields?: DataEnabledTagValueField[];
};

export type DataEnabledTagValueField = {
  name: string;
  type: string;
  key: string;
  value: string;
};

export type CaseExtras = {
  mrn?: string;
};

export type Address = {
  name?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  coordinates?: GpsLocation;
};

export type DispatchEvent = {
  at: Date;
  type: string;
  by: string;
};

export type Organization = {
  id: string;
  name: string;
  location?: GpsLocation;
};

export type HasSupportedFeatures = {
  supportedFeatures?: SupportedFeature[];
};

export type Location = {
  address?: Address;
  hospital?: Organization;
};

export type LocationUpdate = {
  hospital: GpsLocation;
  createdAt: string;
  deviceCreatedAt: string;
} & GpsLocation;

export type DynamicForm = {
  id: string;
  fields: DynamicFormFieldDefinition[];
  name: string;
  title: string;
  subtitle: string;
  type: string;
};

export type DynamicFormSelectOption = {
  value: string;
  name: string;
};

export type DynamicValue = {
  id: string;
  fieldName: string;
  fields: DynamicValueField[];
};

export type DynamicValueField = {
  name: string;
  type: string;
  key: string;
  value: string;
};

export type EmsAgency = {
  members: string[];
  administrators: string[];
  mih?: {
    defaultMapView?: DefaultMapView;
  };
} & Organization &
  HasSupportedFeatures &
  HasDynamicForms &
  HasComplaintSet;

export type IftEmsAgency = {
  hospitals: Hospital[];
  iftComplaintSet: ComplaintSet;
  reportColumns?: IftReportColumn[];
} & Organization &
  HasDynamicForms;

export type Truck = {
  name: string;
  isALS: boolean;
} & HasId;

export enum ProtocolType {
  Case = "case",
  TeamActivation = "teamActivation",
  cad = "cad",
}

export type Protocol = {
  activatedText?: string;
  activatedAt?: string;
  activatedBy?: User;
  color?: string;
  hospitalId?: string;
  confirmationText?: string;
  name: string;
  steps?: ProtocolStep[];
  type: ProtocolType;
  _id: string;
};

export type SupportedFeature = {
  enabled: boolean;
  name: FeatureFlag;
  _id: string;
};

export type ProtocolStep = {
  payload?: string;
  name: string;
  repeatable?: boolean;
  activatedAt?: string;
  activatedBy?: User;
  _id: string;
};

export type RequestBody = Record<string, unknown>;

export type HospitalStatus = {
  _id: string;
  priority: StatusPriority;
  status: string;
  createdAt: string;
  createdBy: string;
};

export type HospitalStatuses = {
  id: string;
  name: string;
  statusChannelId: string;
  activeStatuses: HospitalStatus[];
};

export type HospitalTeam = {
  _id: string;
  name: string;
  members: string[];
};

export enum StatusPriority {
  Urgent = "urgent",
  Critical = "critical",
  Info = "info",
}

export type Feature = {
  name: string;
  enabled: boolean;
  grantedBy?: { id: string }[];
};

export enum MediaType {
  photo = "photo",
  id = "id",
  ekg = "ekg",
  voiceMemo = "voicememo",
  video = "video",
  epcrPdf = "epcr_pdf",
}

export enum TimelineFilterType {
  All = "All",
  Media = "media",
  Text = "text",
}

export enum FileType {
  Image = "image/png",
  Audio = "audio/mp3",
  Video = "video/mp4",
  Pdf = "application/pdf",
}

export enum AllowedKeys {
  pickup = "pickup",
  delivered = "delivery",
}

export type UpdateIFTDatesParams = {
  caseId: string;
  key: AllowedKeys;
  action: CaseAction;
};
